import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSubmitLock } from '../hooks/useSubmitLock';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Insights as InsightsIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend, ResponsiveContainer } from 'recharts';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import { manualSalesPerformanceService } from '../services/manualSalesPerformanceService';
import { useNotification } from '../contexts/NotificationContext';

function useDebouncedValue(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function formatDateTimeBr(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR');
  } catch {
    return String(iso);
  }
}

function toIsoOrUndefined(dtLocalString) {
  if (!dtLocalString) return undefined;
  const d = new Date(dtLocalString);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function toLocalDateTimeInputValue(date) {
  if (!date) return '';
  try {
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

function getDefaultDateRangeLocal() {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 30);
  return {
    start: toLocalDateTimeInputValue(start),
    end: toLocalDateTimeInputValue(now),
  };
}

const COLOR_PALETTE = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1', '#6d4c41'];

export default function ManualSalesPerformanceAnalytics() {
  const [submitting, runSubmit] = useSubmitLock();
  const { showSuccess, showError } = useNotification();

  const defaultRange = useMemo(() => getDefaultDateRangeLocal(), []);

  // Filtros
  const [productToAdd, setProductToAdd] = useState('');
  const [productToAddLabel, setProductToAddLabel] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]); // [{product_id, product_title}]
  const [startDateLocal, setStartDateLocal] = useState(defaultRange.start);
  const [endDateLocal, setEndDateLocal] = useState(defaultRange.end);

  // Filtros avançados
  const [searchText, setSearchText] = useState('');
  const [percentMin, setPercentMin] = useState('');
  const [percentMax, setPercentMax] = useState('');
  const [granularity, setGranularity] = useState('daily');

  const debouncedSearchText = useDebouncedValue(searchText, 450);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Config list
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [total, setTotal] = useState(0);

  // Series
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [series, setSeries] = useState([]);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // config
  const [formProductId, setFormProductId] = useState('');
  const [formProductLabel, setFormProductLabel] = useState('');
  const [formUnitPrice, setFormUnitPrice] = useState('');
  const [formTotalNumbers, setFormTotalNumbers] = useState('');
  const [formNotes, setFormNotes] = useState('');

  const productIds = useMemo(() => selectedProducts.map(p => p.product_id), [selectedProducts]);
  const compareMode = productIds.length >= 2;
  const effectiveGranularity = compareMode ? 'hourly' : granularity;

  const fetchProductsPage = useCallback(async ({ q = '', offset = 0, limit = 50 }) => {
    return await manualSalesPerformanceService.searchProducts({ q, offset, limit });
  }, []);

  const addSelectedProduct = () => {
    const pid = String(productToAdd || '').trim();
    if (!pid) return;
    if (selectedProducts.some(p => p.product_id === pid)) {
      setProductToAdd('');
      setProductToAddLabel('');
      return;
    }
    setSelectedProducts(prev => [...prev, { product_id: pid, product_title: productToAddLabel || pid }]);
    setProductToAdd('');
    setProductToAddLabel('');
  };

  const removeSelectedProduct = (pid) => {
    setSelectedProducts(prev => prev.filter(p => p.product_id !== pid));
  };

  const openCreate = () => {
    setEditing(null);
    setFormProductId(productIds[0] || '');
    setFormProductLabel(selectedProducts.find(p => p.product_id === (productIds[0] || ''))?.product_title || '');
    setFormUnitPrice('');
    setFormTotalNumbers('');
    setFormNotes('');
    setDialogOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setFormProductId(entry.product_id);
    setFormProductLabel(entry.product_title || '');
    setFormUnitPrice(String(entry.unit_price ?? ''));
    setFormTotalNumbers(String(entry.total_numbers ?? ''));
    setFormNotes(entry.notes || '');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await manualSalesPerformanceService.listConfigs({
        product_ids: productIds,
        q: debouncedSearchText || undefined,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setConfigs(res?.data || []);
      setTotal(res?.total || 0);
    } catch (e) {
      showError(e?.message || 'Erro ao carregar configurações');
      setConfigs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [productIds, debouncedSearchText, page, rowsPerPage, showError]);

  const loadSeries = useCallback(async () => {
    if (!productIds.length) {
      setSeries([]);
      return;
    }
    setSeriesLoading(true);
    try {
      const pctMinNum = percentMin === '' ? undefined : Number(percentMin);
      const pctMaxNum = percentMax === '' ? undefined : Number(percentMax);

      const res = await manualSalesPerformanceService.getSeries({
        product_ids: productIds,
        start_date: compareMode ? undefined : toIsoOrUndefined(startDateLocal),
        end_date: compareMode ? undefined : toIsoOrUndefined(endDateLocal),
        percent_min: typeof pctMinNum === 'number' && !Number.isNaN(pctMinNum) ? pctMinNum : undefined,
        percent_max: typeof pctMaxNum === 'number' && !Number.isNaN(pctMaxNum) ? pctMaxNum : undefined,
        granularity: effectiveGranularity
      });
      setSeries(res?.data || []);
    } catch (e) {
      showError(e?.message || 'Erro ao carregar série');
      setSeries([]);
    } finally {
      setSeriesLoading(false);
    }
  }, [productIds, compareMode, effectiveGranularity, startDateLocal, endDateLocal, percentMin, percentMax, showError]);

  useEffect(() => {
    // Carrega sempre que filtros mudam
    loadConfigs();
    loadSeries();
  }, [loadConfigs, loadSeries]);

  // Seleciona automaticamente o sorteio mais recente (por venda) ao recarregar (se existir)
  useEffect(() => {
    if ((selectedProducts || []).length) return;
    if (!(configs || []).length) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await manualSalesPerformanceService.getDefaultProduct();
        const pid = res?.product_id || res?.data?.product_id;
        const title = res?.product_title || res?.data?.product_title;
        if (cancelled) return;
        if (pid) {
          setSelectedProducts([{ product_id: pid, product_title: title || pid }]);
          return;
        }
      } catch {
        // fallback abaixo
      }

      const first = configs[0];
      if (!cancelled && first?.product_id) {
        setSelectedProducts([{ product_id: first.product_id, product_title: first.product_title || first.product_id }]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [configs, selectedProducts]);

  // Quando filtros (exceto página) mudarem, volta para primeira página
  useEffect(() => {
    setPage(0);
  }, [productIds, startDateLocal, endDateLocal, debouncedSearchText, percentMin, percentMax, rowsPerPage]);

  const clearFilters = () => {
    const range = getDefaultDateRangeLocal();
    setStartDateLocal(range.start);
    setEndDateLocal(range.end);
    setSearchText('');
    setPercentMin('');
    setPercentMax('');
    setGranularity('daily');
    setRowsPerPage(25);
    setPage(0);
  };

  const handleSave = async () => {
    const payload = {
      product_id: String(formProductId || '').trim(),
      unit_price: formUnitPrice === '' ? undefined : Number(formUnitPrice),
      total_numbers: formTotalNumbers === '' ? undefined : Number(formTotalNumbers),
      notes: formNotes
    };

    if (!payload.product_id) {
      showError('Selecione um produto/sorteio');
      return;
    }
    if (typeof payload.unit_price !== 'number' || Number.isNaN(payload.unit_price) || payload.unit_price <= 0) {
      showError('Informe um valor unitário válido');
      return;
    }
    if (typeof payload.total_numbers !== 'number' || Number.isNaN(payload.total_numbers) || payload.total_numbers <= 0) {
      showError('Informe a quantidade de números válida');
      return;
    }

    try {
      if (editing?.product_id) {
        await manualSalesPerformanceService.updateConfig(payload.product_id, payload);
        showSuccess('Configuração atualizada');
      } else {
        await manualSalesPerformanceService.createConfig(payload);
        showSuccess('Configuração criada');
      }
      closeDialog();
      await loadConfigs();
      await loadSeries();
    } catch (e) {
      showError(e?.message || 'Erro ao salvar');
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.product_id) return;
    // eslint-disable-next-line no-restricted-globals
    const ok = confirm('Excluir esta configuração?');
    if (!ok) return;
    try {
      await manualSalesPerformanceService.deleteConfig(entry.product_id);
      showSuccess('Configuração excluída');
      await loadConfigs();
      await loadSeries();
    } catch (e) {
      showError(e?.message || 'Erro ao excluir');
    }
  };

  const chartSeries = useMemo(() => {
    return (series || []).map((s, idx) => ({
      product_id: s.product_id,
      product_title: s.product_title || s.product_id,
      color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
      points: (s.points || []).map(p => ({
        x: new Date(p.observed_at).getTime(),
        y: p.percent,
      }))
    }));
  }, [series]);

  const formatRelativeHour = useCallback((hourIndex) => {
    const h = typeof hourIndex === 'number' ? hourIndex : Number(hourIndex);
    if (!Number.isFinite(h)) return String(hourIndex ?? '');
    const day = Math.floor(h / 24) + 1;
    const hour = h % 24;
    return `Dia ${day} • ${String(hour).padStart(2, '0')}:00`;
  }, []);

  const lineData = useMemo(() => {
    const rowsByX = new Map();
    if (compareMode) {
      for (const s of chartSeries) {
        const pts = [...(s.points || [])].sort((a, b) => a.x - b.x);
        const startX = pts.length ? pts[0].x : null;
        if (startX == null) continue;
        for (const p of pts) {
          const relHour = Math.round((p.x - startX) / 3600000);
          const x = relHour;
          if (!rowsByX.has(x)) rowsByX.set(x, { x });
          rowsByX.get(x)[s.product_id] = p.y;
        }
      }
      return Array.from(rowsByX.values()).sort((a, b) => a.x - b.x);
    }

    for (const s of chartSeries) {
      for (const p of (s.points || [])) {
        const x = p.x;
        if (!rowsByX.has(x)) rowsByX.set(x, { x });
        rowsByX.get(x)[s.product_id] = p.y;
      }
    }
    return Array.from(rowsByX.values()).sort((a, b) => a.x - b.x);
  }, [chartSeries, compareMode]);

  const hasData = lineData.length > 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold" color="primary">
              Análises • Desempenho Manual
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Configure valor unitário e quantidade de números para calcular % automaticamente pelo webhook
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadConfigs(); loadSeries(); }}>
              Atualizar
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Nova Configuração
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <AsyncSearchSelect
                label="Adicionar produto/sorteio"
                value={productToAdd}
                selectedLabel={productToAddLabel}
                onChange={(id, opt) => { setProductToAdd(id); setProductToAddLabel(opt?.label || ''); }}
                fetchPage={fetchProductsPage}
                limit={50}
                placeholder="Busque por nome ou ID"
                fullWidth
                sx={{
                  flex: 1,
                  minWidth: { xs: '100%', sm: 360, md: 460 },
                }}
              />
              <Button variant="outlined" onClick={addSelectedProduct} disabled={!productToAdd}>
                Adicionar
              </Button>
            </Stack>
            <Box sx={{ mt: 1 }}>
              {(selectedProducts || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Selecione 1 ou mais produtos para comparar.
                </Typography>
              ) : (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {selectedProducts.map(p => (
                    <Chip
                      key={p.product_id}
                      label={p.product_title || p.product_id}
                      onDelete={() => removeSelectedProduct(p.product_id)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Início (opcional)"
                type="datetime-local"
                value={startDateLocal}
                onChange={(e) => setStartDateLocal(e.target.value)}
                size="small"
                fullWidth
                disabled={compareMode}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Fim (opcional)"
                type="datetime-local"
                value={endDateLocal}
                onChange={(e) => setEndDateLocal(e.target.value)}
                size="small"
                fullWidth
                disabled={compareMode}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  label="Busca (produto/observação)"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="Ex.: GOLF, 21/01, atraso..."
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="% mín"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  value={percentMin}
                  onChange={(e) => setPercentMin(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  label="% máx"
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  value={percentMax}
                  onChange={(e) => setPercentMax(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Escala"
                  select
                  SelectProps={{ native: true }}
                  value={effectiveGranularity}
                  onChange={(e) => setGranularity(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={compareMode}
                >
                  <option value="daily">Diária</option>
                  <option value="hourly">Por hora</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={1.5}>
                <Button
                  variant="text"
                  onClick={clearFilters}
                  sx={{ height: '40px' }}
                >
                  Limpar filtros
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <InsightsIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">Gráfico (comparação)</Typography>
              </Stack>

              {seriesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : !productIds.length ? (
                <Alert severity="info">Selecione pelo menos um produto para visualizar o gráfico.</Alert>
              ) : !hasData ? (
                <Alert severity="warning">Sem pontos no período selecionado.</Alert>
              ) : (
                <Box sx={{ width: '100%', height: 380 }}>
                  <ResponsiveContainer>
                    <LineChart data={lineData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        dataKey="x"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(v) => (compareMode ? formatRelativeHour(v) : new Date(v).toLocaleString('pt-BR'))}
                      />
                      <YAxis type="number" dataKey="y" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <ReTooltip
                        formatter={(value, name, props) => {
                          if (typeof value === 'number') return [`${value.toFixed(2)}%`, name];
                          return [value, name];
                        }}
                        labelFormatter={(label) => (compareMode ? `Período: ${formatRelativeHour(label)}` : `Data/Hora: ${new Date(label).toLocaleString('pt-BR')}`)}
                      />
                      <Legend />
                      {chartSeries.map((s) => (
                        <Line
                          key={s.product_id}
                          type="monotone"
                          dataKey={s.product_id}
                          name={s.product_title}
                          stroke={s.color}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Dica: use o mesmo dropdown para adicionar vários sorteios e comparar as curvas.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Configurações</Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Total: {total}
                  </Typography>
                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Produto</TableCell>
                          <TableCell align="right">Valor unit.</TableCell>
                          <TableCell align="right">Qtd números</TableCell>
                          <TableCell>Obs.</TableCell>
                          <TableCell align="right">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {configs.map((e) => (
                          <TableRow key={e.product_id} hover>
                            <TableCell sx={{ maxWidth: 180 }}>
                              <Tooltip title={e.product_title || e.product_id}>
                                <span>{(e.product_title || e.product_id || '').slice(0, 28)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">R$ {Number(e.unit_price).toFixed(2)}</TableCell>
                            <TableCell align="right">{Number(e.total_numbers)}</TableCell>
                            <TableCell sx={{ maxWidth: 160 }}>
                              <Tooltip title={e.notes || ''}>
                                <span>{(e.notes || '').slice(0, 24)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => openEdit(e)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDelete(e)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        {configs.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5}>
                              <Typography variant="body2" color="text.secondary">
                                Sem configurações para os filtros atuais.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Itens por página"
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar Configuração' : 'Nova Configuração'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <AsyncSearchSelect
              label="Produto/Sorteio"
              value={formProductId}
              selectedLabel={formProductLabel}
              onChange={(id, opt) => { setFormProductId(id); setFormProductLabel(opt?.label || ''); }}
              fetchPage={fetchProductsPage}
              limit={50}
              placeholder="Busque por nome ou ID"
              fullWidth
            />
            <TextField
              label="Valor unitário (R$)"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={formUnitPrice}
              onChange={(e) => setFormUnitPrice(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Quantidade de números"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={formTotalNumbers}
              onChange={(e) => setFormTotalNumbers(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="Observações (opcional)"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              size="small"
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => runSubmit(handleSave)} disabled={submitting}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
